import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

type LoadingVariant = "list" | "ticket" | "board" | "portal";

/** Static shapes reserve content space without pulsing, flashing or shifting focus. */
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={"rounded bg-surface-2 " + className} />;
}

/** Feedback for actions and slower refreshes; each page owns its initial skeleton. */
export function LoadingBoundary({ children }: { children: ReactNode }) {
  const mutations = useIsMutating({ predicate: (m) => m.meta?.background !== true });
  const refreshing = useIsFetching({ predicate: (q) => q.getObserversCount() > 0 && q.state.data !== undefined && q.state.isInvalidated && q.meta?.background !== true });
  const isRefreshing = refreshing > 0;
  const [refreshVisible, setRefreshVisible] = useState(false);
  useEffect(() => {
    if (!isRefreshing) {
      setRefreshVisible(false);
      return;
    }
    const timer = window.setTimeout(() => setRefreshVisible(true), 250);
    return () => window.clearTimeout(timer);
  }, [isRefreshing]);
  const { t } = useTranslation();
  return <>
    {children}
    {(mutations > 0 || (isRefreshing && refreshVisible)) && createPortal(
      <div role="status" aria-live="polite" data-testid="activity-status" className="pointer-events-none fixed top-2 left-1/2 z-[110] flex -translate-x-1/2 items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-muted shadow-sm">
        <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-accent" />
        {t(mutations > 0 ? "common.working" : "common.refreshing")}
      </div>, document.body,
    )}
  </>;
}

/** Inline, responsive placeholders. Keep the surrounding shell and ready controls mounted. */
export function PageLoading({ variant = "list" }: { variant?: LoadingVariant }) {
  const { t } = useTranslation();
  return <section role="status" aria-live="polite" aria-label={t("common.loading")} aria-busy="true" className="min-h-[420px] p-4" data-testid="page-loading" data-variant={variant}>
    <span className="sr-only">{t("common.loading")}</span>
    <div aria-hidden="true" className="space-y-5">
      <div className="flex h-8 items-center justify-between gap-4">
        <Skeleton className={variant === "portal" ? "h-6 w-64 max-w-[75%]" : "h-5 w-40"} />
        {variant !== "portal" && <Skeleton className="h-7 w-20" />}
      </div>
      {variant === "portal" ? <>
        <Skeleton className="h-3 w-56 max-w-[80%]" />
        <Skeleton className="h-[46px] w-full rounded-[10px]" />
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 4 }, (_, i) => <div key={i} className="space-y-2 rounded-[10px] border border-border p-3">
            <Skeleton className="h-3 w-3/4" /><Skeleton className="h-2.5 w-1/2" />
          </div>)}
        </div>
        <div className="space-y-4 pt-2">
          <Skeleton className="h-3 w-28" />
          {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-3 w-4/5" />)}
        </div>
        <Skeleton className="h-24 w-full rounded-[10px]" />
      </> : variant === "ticket" ? <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_260px]">
        <div className="space-y-5">
          <div className="flex items-center gap-3"><Skeleton className="h-9 w-9 shrink-0 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-3 w-36" /><Skeleton className="h-2.5 w-24" /></div></div>
          <div className="space-y-3 rounded-lg border border-border p-5">
            <Skeleton className="h-3 w-11/12" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" /><Skeleton className="h-3 w-2/3" />
          </div>
          <div className="space-y-4 rounded-lg border border-border p-4"><Skeleton className="h-3 w-24" /><Skeleton className="h-32 w-full" /><Skeleton className="h-8 w-24" /></div>
        </div>
        <div className="space-y-5 rounded-lg border border-border p-4">
          {Array.from({ length: 5 }, (_, i) => <div key={i} className="space-y-2"><Skeleton className="h-2.5 w-20" /><Skeleton className="h-7 w-full" /></div>)}
        </div>
      </div> : variant === "board" ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => <div key={i} className="space-y-3 rounded-lg bg-surface-2/40 p-3">
          <Skeleton className="mb-4 h-3 w-28" />
          {Array.from({ length: 3 }, (_, j) => <div key={j} className="space-y-3 rounded-lg border border-border bg-surface p-3"><Skeleton className="h-2.5 w-16" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-3/4" /><Skeleton className="h-5 w-14" /></div>)}
        </div>)}
      </div> : <>
        <div className="flex gap-2"><Skeleton className="h-8 flex-1" /><Skeleton className="h-8 w-20" /></div>
        <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
          {Array.from({ length: 7 }, (_, i) => <div key={i} className="flex min-h-16 items-center gap-3 px-3 py-3">
            <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2"><Skeleton className={i % 2 === 0 ? "h-3 w-3/4" : "h-3 w-1/2"} /><Skeleton className="h-2.5 w-1/3" /></div>
            <Skeleton className="hidden h-5 w-16 sm:block" /><Skeleton className="h-2.5 w-10" />
          </div>)}
        </div>
      </>}
    </div>
  </section>;
}

/** Only used before the staff shell is ready; no full-screen overlay or invented user data. */
export function WorkspaceLoading() {
  const { t } = useTranslation();
  return <div className="flex min-h-dvh flex-col" data-testid="workspace-loading">
    <div aria-hidden="true" className="hidden h-14 items-center gap-4 border-b border-border bg-surface px-3 md:flex">
      <span className="font-semibold">{t("app.name")}</span><Skeleton className="h-7 w-14" /><Skeleton className="ml-auto h-7 w-28" />
    </div>
    <div className="flex flex-1">
      <div aria-hidden="true" className="hidden w-[236px] shrink-0 space-y-5 border-r border-border bg-sf-side p-4 md:block">
        <Skeleton className="h-6 w-24" /><Skeleton className="h-8 w-full" />
        {Array.from({ length: 7 }, (_, i) => <div key={i} className="flex items-center gap-3"><Skeleton className="h-4 w-4" /><Skeleton className="h-3 w-28" /></div>)}
      </div>
      <div className="min-w-0 flex-1"><PageLoading /></div>
    </div>
    <div aria-hidden="true" className="mt-auto flex h-16 items-center justify-around border-t border-border md:hidden">
      {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-6 w-6" />)}
    </div>
  </div>;
}

/** Match the narrow portal frame while its code is downloading, without starting queries. */
export function PortalLoading() {
  const { t } = useTranslation();
  return <div className="min-h-dvh bg-portal-bg text-portal-text">
    <div className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col bg-surface sm:border-x sm:border-portal-border">
      <div aria-hidden="true" className="flex h-[45px] items-center gap-2.5 border-b border-portal-border-2 px-4">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-portal-accent text-[11px] font-bold text-white">U</span>
        <span className="text-[13.5px] font-semibold">{t("app.name")}</span><Skeleton className="ml-auto h-5 w-16" />
      </div>
      <PageLoading variant="portal" />
      <div aria-hidden="true" className="mt-auto border-t border-border p-4"><Skeleton className="ml-auto h-[30px] w-20" /></div>
    </div>
  </div>;
}
